import React from 'react';
import { useTranslation } from 'react-i18next';
import { severityOf } from '../utils/alertTextComposer';

interface HubRisk {
  alertId: number;
  alertTypeCode?: string | null;
  severity?: string | null;
  headline?: string | null;
  handleStatus: string;
  comment?: { content: string } | null;
  actionTaken?: string | null;
}

interface HubRiskItemProps {
  risk: HubRisk;
  /** 本仓仓名，用于剥掉标题前缀；仓卡标题已经写着仓名了 */
  warehouseName?: string;
}

// 上游 title 形如「LA_DEN Warehouse·包裹断更异常」，只留后半截
const stripWarehousePrefix = (title: string, warehouseName?: string): string => {
  if (!warehouseName || !title.startsWith(warehouseName)) {
    return title;
  }
  // 分隔符上游未约定，中英文点号、冒号、连字符都剥
  return title.slice(warehouseName.length).replace(/^[·・:：\-\s]+/, '') || title;
};

// 已处理一律染绿，未闭环按严重度染色
const toneOf = (risk: HubRisk) => {
  if (risk.handleStatus === 'HANDLED') {
    return { bar: 'bg-emerald-500', title: 'text-emerald-700' };
  }
  // 后端 severity 恒为 null，按 alertTypeCode 派生档位（与实时告警卡同一口径）
  const severity = severityOf(risk.severity, risk.alertTypeCode);
  if (severity === 'HIGH' || severity === 'CRITICAL') {
    return { bar: 'bg-red-500', title: 'text-red-600' };
  }
  return { bar: 'bg-amber-400', title: 'text-amber-600' };
};

const statusChip = (handleStatus: string): { key: string; className: string } => {
  switch (handleStatus) {
    case 'HANDLED':
      return { key: 'hubReview.handled', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'IN_PROGRESS':
      return { key: 'hubReview.inProgress', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    default:
      return { key: 'hubReview.unhandled', className: 'bg-red-50 text-red-600 border-red-200' };
  }
};

const HubRiskItem: React.FC<HubRiskItemProps> = ({ risk, warehouseName }) => {
  const { t } = useTranslation();
  const tone = toneOf(risk);
  const chip = statusChip(risk.handleStatus);
  const actionText = risk.comment?.content ?? risk.actionTaken;
  // 只出上游原文（草稿风险的 `title`）。i18n 的 headline 模板全都带插值
  // （`{{dspName}} 有滞留仓内未领取`），上游不给 params，拼出来是缺主语的半句，不如留空
  const title = risk.headline ? stripWarehousePrefix(risk.headline, warehouseName) : null;

  return (
    <div className="relative rounded border border-violet-200 bg-white px-3 py-2 overflow-hidden">
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${tone.bar}`} />
      <div className="flex items-center gap-2 mb-1">
        {title && <span className={`text-sm font-semibold ${tone.title}`}>{title}</span>}
        <span className={`text-[11px] px-1.5 py-px rounded border ${chip.className}`}>
          {String(t(chip.key))}
        </span>
      </div>
      {actionText && (
        <p className="text-xs text-gray-600 leading-relaxed mt-1">
          <span className="font-medium text-gray-500">{String(t('hubReview.handledPrefix'))}</span>
          {actionText}
        </p>
      )}
    </div>
  );
};

export default HubRiskItem;
